/**
 * Matches listing — uses PageHeader, Card, Badge, Skeleton, EmptyState, Sheet for mobile filters.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Filter, Clock, X } from 'lucide-react';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/card';
import Badge, { type BadgeProps } from '../components/ui/badge';
import PageHeader from '../components/ui/page-header';
import EmptyState from '../components/ui/empty-state';
import Skeleton from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '../components/ui/sheet';
import Separator from '../components/ui/separator';
import { useMatches } from '../hooks/useMatches';
import {
  cn,
  formatMatchDate,
  formatTime,
  getLocalDayKey,
  getStageDisplayName,
  getStatusDisplay,
  parseLocalDayKey,
} from '../lib/utils';
import { STAGES, MATCH_STATUSES } from '../lib/constants';
import type { Match } from '../../shared/types';
import { useSEO, SEO_CONFIGS } from '../hooks/useSEO';

const ALL_STAGES = '__all__';
const ALL_STATUSES = '__all__';

export default function MatchesPage() {
  useSEO(SEO_CONFIGS.matches);

  const [stageFilter, setStageFilter] = useState<string>(ALL_STAGES);
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);

  const { data, isLoading } = useMatches({
    stage: stageFilter === ALL_STAGES ? undefined : stageFilter,
    status: statusFilter === ALL_STATUSES ? undefined : statusFilter,
    limit: 50,
  });

  const matches = data?.data || [];
  const filterCount =
    (stageFilter !== ALL_STAGES ? 1 : 0) + (statusFilter !== ALL_STATUSES ? 1 : 0);

  const matchesByDate = matches.reduce<Record<string, Match[]>>((acc, match) => {
    const dayKey = getLocalDayKey(match.matchDate);
    if (!acc[dayKey]) acc[dayKey] = [];
    acc[dayKey].push(match);
    return acc;
  }, {});

  const clearFilters = () => {
    setStageFilter(ALL_STAGES);
    setStatusFilter(ALL_STATUSES);
  };

  const filterControls = (
    <>
      <div className="space-y-2">
        <label className="block text-xs font-medium text-muted-foreground">Fase</label>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Todas as fases" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STAGES}>Todas as fases</SelectItem>
            {STAGES.map((stage) => (
              <SelectItem key={stage.value} value={stage.value}>
                {stage.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-muted-foreground">Status</label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Todos os status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>Todos os status</SelectItem>
            {MATCH_STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        title="Jogos"
        description="Acompanhe o calendário oficial da Copa do Mundo 2026."
        actions={
          <div className="flex items-center gap-2">
            {/* Mobile: filters via Sheet */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Filter />}
                  className="sm:hidden"
                >
                  Filtros
                  {filterCount > 0 && (
                    <Badge variant="default" className="ml-2 px-1.5">
                      {filterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-2xl">
                <SheetHeader>
                  <SheetTitle>Filtros</SheetTitle>
                  <SheetDescription>Refine a lista de jogos.</SheetDescription>
                </SheetHeader>
                <div className="space-y-4 mt-6">
                  {filterControls}
                  <Separator />
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={clearFilters}>
                      Limpar
                    </Button>
                    <SheetClose asChild>
                      <Button className="flex-1">Aplicar</Button>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            {filterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                icon={<X />}
                onClick={clearFilters}
                className="hidden sm:inline-flex"
              >
                Limpar
              </Button>
            )}
          </div>
        }
      />

      {/* Desktop filters */}
      <Card className="p-4 mb-6 hidden sm:block">
        <div className="grid sm:grid-cols-2 gap-4">{filterControls}</div>
      </Card>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
      ) : matches.length === 0 ? (
        <EmptyState
          icon={<Calendar className="w-6 h-6" aria-hidden="true" />}
          title="Nenhum jogo encontrado"
          description="Ajuste os filtros ou aguarde o próximo jogo do calendário oficial."
        />
      ) : (
        <div className="space-y-8">
          {Object.entries(matchesByDate).map(([date, dateMatches]) => (
            <section key={date}>
              <h2 className="sticky top-16 z-10 -mx-4 mb-4 bg-background/95 px-4 py-2 text-sm font-semibold text-muted-foreground backdrop-blur-md sm:top-20 inline-flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary-500 dark:text-primary-400" aria-hidden="true" />
                {formatMatchDate(parseLocalDayKey(date))}
              </h2>
              <div className="space-y-3">
                {dateMatches.map((match, index) => (
                  <MatchCard key={match.id} match={match} index={index} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function statusBadgeVariant(status: string): NonNullable<BadgeProps['variant']> {
  switch (status) {
    case 'live':
      return 'destructive';
    case 'finished':
      return 'secondary';
    case 'postponed':
    case 'cancelled':
      return 'warning';
    case 'scheduled':
    default:
      return 'info';
  }
}

function MatchCard({ match, index }: { match: Match; index: number }) {
  const status = getStatusDisplay(match.status);
  const isFinished = match.status === 'finished';
  const isLive = match.status === 'live';

  return (
    <Link
      to={`/jogos/${match.id}`}
      className="group block rounded-2xl animate-fade-in focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      style={{ animationDelay: `${Math.min(index * 30, 250)}ms` }}
    >
      <Card className="p-4 transition-all duration-200 hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-md group-hover:-translate-y-0.5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-muted-foreground">
            {getStageDisplayName(match.stage)}
          </span>
          <Badge variant={statusBadgeVariant(match.status)}>
            {isLive && (
              <span
                className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse mr-1"
                aria-hidden="true"
              />
            )}
            {status.label}
          </Badge>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0 text-right font-medium text-foreground truncate">
            {match.homeTeam}
          </div>
          {isFinished || isLive ? (
            <div className="flex items-center gap-2 rounded-xl bg-muted px-4 py-2 tabular-nums">
              <span className="text-2xl font-bold text-foreground">{match.homeScore}</span>
              <span className="text-muted-foreground">×</span>
              <span className="text-2xl font-bold text-foreground">{match.awayScore}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-muted-foreground">
              <Clock className="w-4 h-4" aria-hidden="true" />
              <span className="font-medium tabular-nums">{formatTime(match.matchDate)}</span>
            </div>
          )}
          <div className={cn('flex-1 min-w-0 text-left font-medium text-foreground truncate')}>
            {match.awayTeam}
          </div>
        </div>
      </Card>
    </Link>
  );
}
